import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import StaffRegistrationForm from '../components/StaffRegistrationForm';
import ResidentRegistrationForm from '../components/ResidentRegistrationForm';
import { dailyCareLogsApi } from '../api/dailyCareLogs';

interface DashboardStats {
  totalStaff: number;
  totalResidents: number;
  totalNotes: number;
  staffBreakdown: {
    regular: number;
    bank: number;
    agency: number;
  };
  dailyCareStats: {
    todayCompleted: number;
    todayTotal: number;
    todayPercentage: number;
    weeklyTrend: Array<{ date: string; count: number }>;
  };
  trainingStats: {
    total: number;
    completed: number;
    pending: number;
    expired: number;
    mandatoryCourses: number;
    complianceRate: number;
  };
  incidentStats: {
    thisWeek: number;
    thisMonth: number;
    open: number;
    bySeverity: {
      low: number;
      medium: number;
      high: number;
      critical: number;
    };
  };
  carePlanStats: {
    totalActive: number;
    achieved: number;
    dueForReview: number;
  };
  assessmentStats: {
    thisMonth: number;
    byRisk: {
      low: number;
      medium: number;
      high: number;
    };
  };
  upcomingAppointments: Array<{
    id: number;
    title: string;
    appointmentDate: string;
    residentName: string;
    type: string;
    status: string;
  }>;
  urgentTasks: Array<{
    id: number;
    title: string;
    priority: string;
    status: string;
    dueDate?: string;
    assignedToName?: string;
    category: string;
  }>;
  handoverNotes: Array<{
    id: number;
    content: string;
    priority: string;
    residentName: string;
    residentAge: number;
    residentDiagnosis: string;
    residentPhoto?: string;
    authorName: string;
    authorRole: string;
    createdAt: string;
    type: string;
  }>;
}

const Dashboard: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [showResidentForm, setShowResidentForm] = useState(false);
  const [missingLogsCount, setMissingLogsCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (isAdmin) {
          const response = await api.get('/users/dashboard-stats');
          setStats(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const today = new Date().toISOString().slice(0, 10);
    dailyCareLogsApi
      .missingLogs(today)
      .then(r => setMissingLogsCount(r.count))
      .catch(() => setMissingLogsCount(null));
  }, [isAdmin]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleStaffRegistrationSuccess = () => {
    if (isAdmin && stats) {
      setStats(prev => prev ? { ...prev, totalStaff: prev.totalStaff + 1 } : null);
    }
  };

  const handleResidentRegistrationSuccess = () => {
    if (isAdmin && stats) {
      setStats(prev => prev ? { ...prev, totalResidents: prev.totalResidents + 1 } : null);
    }
  };

  const ProgressRing = ({ percentage, size = 80, strokeWidth = 8, color = 'blue' }: { percentage: number; size?: number; strokeWidth?: number; color?: string }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;
    
    const colorClasses: { [key: string]: string } = {
      purple: 'text-primary-600',
      green: 'text-green-600',
      blue: 'text-primary-600',
      orange: 'text-orange-600',
      red: 'text-red-600'
    };

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            className="text-gray-200"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className={colorClasses[color] || 'text-primary-600'}
          />
        </svg>
        <span className="absolute text-lg font-bold text-gray-700">{percentage}%</span>
      </div>
    );
  };

  const MiniBarChart = ({ data, maxValue }: { data: number[]; maxValue: number }) => {
    return (
      <div className="flex items-end space-x-1 h-12">
        {data.map((value, index) => (
          <div
            key={index}
            className="w-3 bg-primary-500 rounded-t"
            style={{ height: `${maxValue > 0 ? (value / maxValue) * 100 : 0}%`, minHeight: value > 0 ? '4px' : '0' }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Gradient Header — matches mobile HomeScreen header exactly */}
      <div className="gradient-header text-white px-5 py-6 sm:py-8"
           style={{ borderRadius: '20px' }}>
        <div className="flex items-center gap-3.5">
          <div className="flex-shrink-0 relative">
            {user?.photo ? (
              <img src={user.photo} alt={user.name} className="w-[62px] h-[62px] rounded-full object-cover"
                   style={{ borderWidth: '2px', borderColor: 'rgba(255,255,255,0.5)', borderStyle: 'solid' }} />
            ) : (
              <div className="w-[62px] h-[62px] rounded-full bg-white/20 flex items-center justify-center"
                   style={{ borderWidth: '2px', borderColor: 'rgba(255,255,255,0.5)', borderStyle: 'solid' }}>
                <span className="text-2xl font-bold text-white">{user?.name?.charAt(0).toUpperCase()}</span>
              </div>
            )}
            {/* Online dot — matches mobile: w14 h14, borderRadius 7, borderWidth 2 */}
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-success rounded-full" style={{ borderWidth: '2px', borderColor: '#FFF', borderStyle: 'solid' }}></div>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold">
              Welcome back, {user?.name}
            </h2>
            <p className="mt-0.5 text-sm font-normal" style={{ color: 'rgba(255,255,255,0.75)' }}>
              {formatDate(new Date().toISOString())}
            </p>
          </div>
        </div>
      </div>

      {isAdmin && missingLogsCount !== null && (
        <button
          onClick={() => navigate('/daily-care-logs/compliance')}
          className={`w-full text-left bg-white overflow-hidden shadow rounded-lg border-l-4 hover:shadow-md transition-shadow ${
            missingLogsCount > 0 ? 'border-red-500' : 'border-green-500'
          }`}
        >
          <div className="p-5 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-600 uppercase tracking-wider">Residents missing a daily care log today</div>
              <div
                className={`text-3xl font-bold mt-1 ${
                  missingLogsCount > 0 ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {missingLogsCount === 0 ? 'All submitted ✓' : `${missingLogsCount} missing`}
              </div>
            </div>
            <span className="text-sm text-blue-600">View compliance →</span>
          </div>
        </button>
      )}

      {isAdmin && (
        <>
          {/* Main Stats Cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden card-list">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-[52px] h-[52px] bg-primary-600 flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Staff</dt>
                      <dd className="text-2xl font-bold text-gray-900">
                        {loading ? '...' : stats?.totalStaff || 0}
                      </dd>
                      {stats?.staffBreakdown && (
                        <dd className="text-xs text-gray-500 mt-1">
                          {stats.staffBreakdown.regular} Regular • {stats.staffBreakdown.bank} Bank • {stats.staffBreakdown.agency} Agency
                        </dd>
                      )}
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden card-list">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-[52px] h-[52px] bg-success flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Residents</dt>
                      <dd className="text-2xl font-bold text-gray-900">
                        {loading ? '...' : stats?.totalResidents || 0}
                      </dd>
                      <dd className="text-xs text-gray-500 mt-1">Active in care</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden card-list">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-[52px] h-[52px] bg-primary-500 flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm0 6h6v2H7v-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Notes</dt>
                      <dd className="text-2xl font-bold text-gray-900">
                        {loading ? '...' : stats?.totalNotes || 0}
                      </dd>
                      <dd className="text-xs text-gray-500 mt-1">Care documentation</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden card-list">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-[52px] h-[52px] flex items-center justify-center ${
                      (stats?.incidentStats?.open || 0) > 0 ? 'bg-red-500' : 'bg-yellow-500'
                    }`}>
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Open Incidents</dt>
                      <dd className="text-2xl font-bold text-gray-900">
                        {loading ? '...' : stats?.incidentStats?.open || 0}
                      </dd>
                      <dd className="text-xs text-gray-500 mt-1">
                        {stats?.incidentStats?.thisMonth || 0} this month
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Analytics Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3.5">
            {/* Daily Care Progress */}
            <div className="bg-white card-hero p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Daily Care Progress</h3>
                <button 
                  onClick={() => navigate('/daily-care')}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  View All
                </button>
              </div>
              <div className="flex items-center justify-center mb-4">
                <ProgressRing 
                  percentage={stats?.dailyCareStats?.todayPercentage || 0} 
                  size={100} 
                  color="blue"
                />
              </div>
              <div className="text-center mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{stats?.dailyCareStats?.todayCompleted || 0}</span> of{' '}
                  <span className="font-semibold text-gray-900">{stats?.dailyCareStats?.todayTotal || 0}</span> tasks completed today
                </p>
              </div>
              {stats?.dailyCareStats?.weeklyTrend && stats.dailyCareStats.weeklyTrend.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">7-Day Trend</p>
                  <MiniBarChart 
                    data={stats.dailyCareStats.weeklyTrend.map(d => d.count)}
                    maxValue={Math.max(...stats.dailyCareStats.weeklyTrend.map(d => d.count), 1)}
                  />
                </div>
              )}
            </div>

            {/* Training Compliance */}
            <div className="bg-white card-hero p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Training Compliance</h3>
                <button 
                  onClick={() => navigate('/training')}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  View All
                </button>
              </div>
              <div className="flex items-center justify-center mb-4">
                <ProgressRing 
                  percentage={stats?.trainingStats?.complianceRate || 0} 
                  size={100} 
                  color="green"
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Completed</span>
                  <span className="font-medium text-green-600">{stats?.trainingStats?.completed || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Pending</span>
                  <span className="font-medium text-yellow-600">{stats?.trainingStats?.pending || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Expired</span>
                  <span className="font-medium text-red-600">{stats?.trainingStats?.expired || 0}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-600">Mandatory Courses</span>
                  <span className="font-medium text-gray-900">{stats?.trainingStats?.mandatoryCourses || 0}</span>
                </div>
              </div>
            </div>

            {/* Care Plans Overview */}
            <div className="bg-white card-hero p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Care Plans</h3>
                <button 
                  onClick={() => navigate('/care-plans')}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  View All
                </button>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600">{stats?.carePlanStats?.totalActive || 0}</div>
                  <div className="text-xs text-gray-500">Active Goals</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{stats?.carePlanStats?.achieved || 0}</div>
                  <div className="text-xs text-gray-500">Achieved</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${(stats?.carePlanStats?.dueForReview || 0) > 0 ? 'text-orange-600' : 'text-gray-600'}`}>
                    {stats?.carePlanStats?.dueForReview || 0}
                  </div>
                  <div className="text-xs text-gray-500">Due Review</div>
                </div>
              </div>
              {(stats?.carePlanStats?.dueForReview || 0) > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-800">
                    <svg className="inline w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {stats?.carePlanStats?.dueForReview || 0} goal{(stats?.carePlanStats?.dueForReview || 0) > 1 ? 's' : ''} need review
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Second Row Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
            {/* Incident Analysis */}
            <div className="bg-white card-hero p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Incident Analysis</h3>
                <button 
                  onClick={() => navigate('/incidents')}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  View All
                </button>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-gray-900">{stats?.incidentStats?.thisWeek || 0}</div>
                  <div className="text-xs text-gray-500">This Week</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-gray-900">{stats?.incidentStats?.thisMonth || 0}</div>
                  <div className="text-xs text-gray-500">This Month</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className={`text-xl font-bold ${(stats?.incidentStats?.open || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {stats?.incidentStats?.open || 0}
                  </div>
                  <div className="text-xs text-gray-500">Open</div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">By Severity (This Month)</p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="w-16 text-xs text-gray-600">Low</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${stats?.incidentStats?.thisMonth ? ((stats.incidentStats.bySeverity.low / stats.incidentStats.thisMonth) * 100) : 0}%` }}
                      />
                    </div>
                    <span className="w-8 text-xs text-right text-gray-600">{stats?.incidentStats?.bySeverity?.low || 0}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-16 text-xs text-gray-600">Medium</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-yellow-500 rounded-full"
                        style={{ width: `${stats?.incidentStats?.thisMonth ? ((stats.incidentStats.bySeverity.medium / stats.incidentStats.thisMonth) * 100) : 0}%` }}
                      />
                    </div>
                    <span className="w-8 text-xs text-right text-gray-600">{stats?.incidentStats?.bySeverity?.medium || 0}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-16 text-xs text-gray-600">High</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-orange-500 rounded-full"
                        style={{ width: `${stats?.incidentStats?.thisMonth ? ((stats.incidentStats.bySeverity.high / stats.incidentStats.thisMonth) * 100) : 0}%` }}
                      />
                    </div>
                    <span className="w-8 text-xs text-right text-gray-600">{stats?.incidentStats?.bySeverity?.high || 0}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-16 text-xs text-gray-600">Critical</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-500 rounded-full"
                        style={{ width: `${stats?.incidentStats?.thisMonth ? ((stats.incidentStats.bySeverity.critical / stats.incidentStats.thisMonth) * 100) : 0}%` }}
                      />
                    </div>
                    <span className="w-8 text-xs text-right text-gray-600">{stats?.incidentStats?.bySeverity?.critical || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Assessments Overview */}
            <div className="bg-white card-hero p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Assessments Overview</h3>
                <button 
                  onClick={() => navigate('/assessments')}
                  className="text-sm text-primary-600 hover:text-primary-800"
                >
                  View All
                </button>
              </div>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-primary-600">{stats?.assessmentStats?.thisMonth || 0}</div>
                <div className="text-sm text-gray-500">Assessments This Month</div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Risk Level Distribution</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xl font-bold text-green-600">{stats?.assessmentStats?.byRisk?.low || 0}</div>
                    <div className="text-xs text-green-700">Low Risk</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-xl font-bold text-yellow-600">{stats?.assessmentStats?.byRisk?.medium || 0}</div>
                    <div className="text-xs text-yellow-700">Medium Risk</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-xl font-bold text-red-600">{stats?.assessmentStats?.byRisk?.high || 0}</div>
                    <div className="text-xs text-red-700">High Risk</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Upcoming Appointments and Urgent Tasks */}
          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
              {/* Upcoming Appointments */}
              <div className="bg-white card-list overflow-hidden">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Upcoming Appointments
                  </h3>
                  {stats.upcomingAppointments.length === 0 ? (
                    <p className="text-sm text-gray-500">No upcoming appointments in the next 7 days.</p>
                  ) : (
                    <div className="space-y-3">
                      {stats.upcomingAppointments.map((appointment) => (
                        <div key={appointment.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{appointment.title}</p>
                            <p className="text-sm text-gray-600">
                              {appointment.residentName} • {formatDate(appointment.appointmentDate)}
                            </p>
                            <p className="text-xs text-gray-500 capitalize">
                              {appointment.type} • {appointment.status}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {new Date(appointment.appointmentDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Urgent Tasks */}
              <div className="bg-white card-list overflow-hidden">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Urgent Tasks
                  </h3>
                  {stats.urgentTasks.length === 0 ? (
                    <p className="text-sm text-gray-500">No urgent tasks at the moment.</p>
                  ) : (
                    <div className="space-y-3">
                      {stats.urgentTasks.map((task) => (
                        <div key={task.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{task.title}</p>
                            <p className="text-sm text-gray-600">
                              {task.assignedToName ? `Assigned to: ${task.assignedToName}` : 'Unassigned'}
                            </p>
                            <p className="text-xs text-gray-500 capitalize">
                              {task.category} • {task.status}
                              {task.dueDate && ` • Due: ${formatDate(task.dueDate)}`}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              task.priority === 'urgent' 
                                ? 'bg-red-100 text-red-800'
                                : task.priority === 'high'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {task.priority}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Handover Notes */}
          {stats && (
            <div className="bg-white card-list overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Recent Handover Notes (Last 24 Hours)
                </h3>
                {stats.handoverNotes.length === 0 ? (
                  <p className="text-sm text-gray-500">No handover notes in the last 24 hours.</p>
                ) : (
                  <div className="space-y-4">
                    {stats.handoverNotes.map((note) => (
                      <div key={note.id} className="border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0">
                            {note.residentPhoto ? (
                              <img 
                                src={note.residentPhoto} 
                                alt={note.residentName}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-100"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center border-2 border-gray-100">
                                <span className="text-blue-600 font-semibold text-lg">
                                  {note.residentName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-3">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                note.type === 'handover'
                                  ? 'bg-primary-100 text-primary-800'
                                  : note.type === 'shift-report'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {note.type === 'handover' ? 'Handover' : note.type === 'shift-report' ? 'Shift Report' : 'Urgent'}
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                note.priority === 'urgent'
                                  ? 'bg-red-100 text-red-800'
                                  : note.priority === 'high'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {note.priority}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(note.createdAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3 mb-3">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <div>
                                  <span className="font-semibold text-blue-800">{note.residentName}</span>
                                </div>
                                <div className="text-blue-700">
                                  <span className="text-gray-600">Age:</span> {note.residentAge}
                                </div>
                                <div className="text-blue-700 md:col-span-1 col-span-2">
                                  <span className="text-gray-600">Diagnosis:</span> 
                                  <span className="ml-1 font-medium">{note.residentDiagnosis}</span>
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 mb-3">
                              <p className="text-sm text-gray-900 leading-relaxed">{note.content}</p>
                            </div>
                            <div className="flex items-center text-xs text-gray-500">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium">{note.authorName}</span>
                              <span className="mx-2">•</span>
                              <span className="capitalize">{note.authorRole}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <div className="bg-white card-list overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Quick Actions</h3>
          <div className="mt-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {isAdmin && (
                <>
                  <button 
                    onClick={() => setShowStaffForm(true)}
                    className="relative group bg-white p-6 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 rounded-lg border border-gray-300 hover:bg-gray-50"
                  >
                    <div>
                      <span className="rounded-lg inline-flex p-3 bg-primary-50 text-primary-600 ring-4 ring-white">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                      </span>
                    </div>
                    <div className="mt-8">
                      <h3 className="text-lg font-medium text-gray-900">Register Staff</h3>
                      <p className="mt-2 text-sm text-gray-500">Add new staff members to the system</p>
                    </div>
                  </button>

                  <button 
                    onClick={() => setShowResidentForm(true)}
                    className="relative group bg-white p-6 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 rounded-lg border border-gray-300 hover:bg-gray-50"
                  >
                    <div>
                      <span className="rounded-lg inline-flex p-3 bg-green-50 text-green-600 ring-4 ring-white">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </span>
                    </div>
                    <div className="mt-8">
                      <h3 className="text-lg font-medium text-gray-900">Register Resident</h3>
                      <p className="mt-2 text-sm text-gray-500">Add new residents to care planning</p>
                    </div>
                  </button>
                </>
              )}

              <button 
                onClick={() => navigate('/daily-care')}
                className="relative group bg-white p-6 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-yellow-50 text-yellow-600 ring-4 ring-white">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </span>
                </div>
                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-900">Daily Care</h3>
                  <p className="mt-2 text-sm text-gray-500">Track daily care activities</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Modals */}
      <StaffRegistrationForm
        isOpen={showStaffForm}
        onClose={() => setShowStaffForm(false)}
        onSuccess={handleStaffRegistrationSuccess}
      />
      
      <ResidentRegistrationForm
        isOpen={showResidentForm}
        onClose={() => setShowResidentForm(false)}
        onSuccess={handleResidentRegistrationSuccess}
      />
    </div>
  );
};

export default Dashboard;
